import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPinned, FlaskConical, Receipt, Handshake, Layers, Info, Activity } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const REGION_SHORT = {
  'National Capital Region (NCR)': 'NCR',
  'Cordillera Administrative Region (CAR)': 'CAR',
  'Region I – Ilocos Region': 'I',
  'Region II – Cagayan Valley': 'II',
  'Region III – Central Luzon': 'III',
  'Region IV-A – CALABARZON': 'IV-A',
  'Region IV-B – MIMAROPA': 'IV-B',
  'Region V – Bicol Region': 'V',
  'Region VI – Western Visayas': 'VI',
  'Region VII – Central Visayas': 'VII',
  'Region VIII – Eastern Visayas': 'VIII',
  'Region IX – Zamboanga Peninsula': 'IX',
  'Region X – Northern Mindanao': 'X',
  'Region XI – Davao Region': 'XI',
  'Region XII – SOCCSKSARGEN': 'XII',
  'Region XIII – Caraga': 'XIII',
  'BARMM – Bangsamoro': 'BARMM',
};

const GROUPS = [
  { name: 'Luzon', regions: ['Cordillera Administrative Region (CAR)', 'Region I – Ilocos Region', 'Region II – Cagayan Valley', 'Region III – Central Luzon', 'National Capital Region (NCR)', 'Region IV-A – CALABARZON', 'Region IV-B – MIMAROPA', 'Region V – Bicol Region'] },
  { name: 'Visayas', regions: ['Region VI – Western Visayas', 'Region VII – Central Visayas', 'Region VIII – Eastern Visayas'] },
  { name: 'Mindanao', regions: ['Region IX – Zamboanga Peninsula', 'Region X – Northern Mindanao', 'Region XI – Davao Region', 'Region XII – SOCCSKSARGEN', 'Region XIII – Caraga', 'BARMM – Bangsamoro'] },
];

const INTENTS = [
  { key: 'total', label: 'All Inquiries', icon: Layers },
  { key: 'samples', label: 'Free Sample', icon: FlaskConical },
  { key: 'pricing', label: 'Pricing & Quote', icon: Receipt },
  { key: 'subdealer', label: 'Sub-Dealer', icon: Handshake },
];

const lerp = (a, b, t) => Math.round(a + (b - a) * t);
// 3-stop heat ramp: sky (cold) -> amber -> red (hot)
const heatColor = (t) => {
  const stops = [[56, 189, 248], [245, 158, 11], [239, 68, 68]];
  const seg = t <= 0.5 ? 0 : 1;
  const lt = t <= 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
  const [r1, g1, b1] = stops[seg];
  const [r2, g2, b2] = stops[seg + 1];
  return [lerp(r1, r2, lt), lerp(g1, g2, lt), lerp(b1, b2, lt)];
};

const fade = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = { show: { transition: { staggerChildren: 0.06 } } };

const RegionTile = ({ region, count, max, intentLabel, active, dark, onClick }) => {
  const t = max > 0 ? count / max : 0;
  const filled = count > 0;
  const [r, g, b] = heatColor(filled ? Math.max(t, 0.08) : 0);
  const bg = filled ? `rgba(${r},${g},${b},${0.18 + t * 0.66})` : (dark ? 'rgba(148,163,184,0.05)' : 'rgba(148,163,184,0.1)');
  const border = active ? '#E87A20' : (filled ? `rgba(${r},${g},${b},0.85)` : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(100,116,139,0.22)'));
  const textCol = filled && t > 0.42 ? '#fff' : (dark ? '#e2e8f0' : '#0f172a');
  const glow = active
    ? '0 0 0 1px #E87A20, 0 8px 28px -6px rgba(232,122,32,0.6)'
    : (filled ? `0 8px 26px -10px rgba(${r},${g},${b},0.8)` : 'none');
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 420, damping: 26 }}
      data-testid={`region-tile-${REGION_SHORT[region]}`}
      title={`${region} — ${count} ${intentLabel.toLowerCase()}`}
      className="group relative flex h-[72px] w-[86px] flex-col items-center justify-center rounded-lg border sm:h-[80px] sm:w-[98px]"
      style={{ backgroundColor: bg, borderColor: border, boxShadow: glow }}
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.12em] opacity-75" style={{ color: textCol }}>{REGION_SHORT[region]}</span>
      <span className="text-2xl font-black leading-none tracking-tight" style={{ color: textCol }}>{count}</span>
    </motion.button>
  );
};

const TerritoryInsightsPage = () => {
  const { theme } = useTheme();
  const dark = theme !== 'light';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intent, setIntent] = useState('total');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.title = 'Territory Coverage Heatmap | FlexiOss® Insights';
    let active = true;
    fetch(`${BACKEND}/api/insights/territory`)
      .then((r) => r.json())
      .then((d) => { if (active) { setData(d); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const regionData = data?.regions || {};
  const countFor = (region) => (regionData[region] ? regionData[region][intent] || 0 : 0);
  const allRegions = useMemo(() => GROUPS.flatMap((g) => g.regions), []);
  const max = Math.max(1, ...allRegions.map(countFor));
  const intentLabel = INTENTS.find((i) => i.key === intent)?.label || 'Inquiries';

  const kpis = [
    { key: 'total', label: 'Total Inquiries', value: data?.total || 0, color: '#E87A20' },
    { key: 'samples', label: 'Free Sample', value: data?.intent_totals?.samples || 0, color: '#38bdf8' },
    { key: 'pricing', label: 'Pricing & Quote', value: data?.intent_totals?.pricing || 0, color: '#fbbf24' },
    { key: 'subdealer', label: 'Sub-Dealer', value: data?.intent_totals?.subdealer || 0, color: '#a78bfa' },
  ];

  const noRegion = data?.unspecified?.[intent] || 0;
  const selectedProvinces = selected && regionData[selected]
    ? Object.entries(regionData[selected].provinces || {}).map(([p, v]) => ({ p, c: v[intent] || 0 })).filter((x) => x.c > 0).sort((a, b) => b.c - a.c)
    : [];

  const pageBg = dark ? 'text-slate-100' : 'bg-slate-50 text-slate-900';
  const glass = dark ? 'border-white/10 bg-white/[0.04]' : 'border-slate-200 bg-white';
  const matte = dark ? 'border-white/10 bg-[#00101f]/70' : 'border-slate-200 bg-white';

  return (
    <div data-testid="territory-insights-page" className={`relative min-h-screen ${pageBg} transition-colors duration-300`} style={dark ? { background: 'linear-gradient(180deg,#001a33 0%,#00060f 60%)' } : {}}>
      {/* Ambient glows (dark) */}
      {dark && (
        <>
          <div className="pointer-events-none fixed -top-24 right-0 h-80 w-80 rounded-full bg-[#E87A20]/10 blur-[130px]" />
          <div className="pointer-events-none fixed bottom-0 left-1/4 h-72 w-72 rounded-full bg-blue-600/10 blur-[130px]" />
        </>
      )}

      {/* Header */}
      <header className={`sticky top-0 z-30 border-b backdrop-blur-xl ${dark ? 'border-white/10 bg-[#001a33]/70' : 'border-slate-200 bg-white/80'}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <Link to="/" data-testid="territory-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 transition-colors hover:text-blue-300">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#E87A20]">
            <MapPinned className="h-4 w-4" /> Territory Coverage
          </span>
        </div>
      </header>

      <main className="relative mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-20">
        <motion.div variants={fade} initial="hidden" animate="show" className="mb-12 max-w-3xl">
          <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
            <Activity className="h-3.5 w-3.5" /> Live Demand Intelligence
          </p>
          <h1 className="text-4xl font-light tracking-tighter sm:text-5xl">Lead Territory Heatmap</h1>
          <p className="mt-4 text-base leading-relaxed text-slate-400 md:text-lg">
            Where clinical demand for FlexiOss® is concentrating across the Philippines — broken down by respondent intent.
            Aggregated counts only; no personal details are shown.
          </p>
        </motion.div>

        {/* KPI row */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {kpis.map((k) => (
            <motion.div key={k.key} variants={fade} data-testid={`kpi-${k.key}`} className={`rounded-xl border p-5 ${glass}`}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{k.label}</p>
              <p className="mt-2 text-4xl font-black tracking-tighter sm:text-5xl" style={{ color: k.color }}>{k.value}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Intent filter — sticky capsule row */}
        <div className={`sticky top-[57px] z-20 -mx-2 mb-7 flex flex-wrap gap-2 rounded-2xl px-2 py-2 backdrop-blur-md ${dark ? 'bg-[#001a33]/50' : 'bg-slate-50/70'}`}>
          {INTENTS.map((i) => {
            const Icon = i.icon;
            const on = intent === i.key;
            return (
              <button
                key={i.key}
                onClick={() => { setIntent(i.key); setSelected(null); }}
                data-testid={`intent-filter-${i.key}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E87A20] ${
                  on ? 'bg-[#E87A20] text-white shadow-[0_6px_20px_-6px_rgba(232,122,32,0.7)]' : `${dark ? 'border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10' : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-100'}`
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {i.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-28 text-slate-500">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#E87A20] border-t-transparent" />
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-12">
            {/* Heat cartogram */}
            <motion.div variants={fade} initial="hidden" animate="show" data-testid="territory-heatmap" className={`rounded-2xl border p-6 sm:p-8 lg:col-span-8 ${matte}`}>
              <div className="grid gap-8 sm:grid-cols-3">
                {GROUPS.map((g) => (
                  <div key={g.name}>
                    <p className={`mb-4 border-b pb-2 text-[11px] font-bold uppercase tracking-[0.24em] ${dark ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'}`}>{g.name}</p>
                    <div className="flex flex-wrap gap-2.5">
                      {g.regions.map((region) => (
                        <RegionTile
                          key={region}
                          region={region}
                          count={countFor(region)}
                          max={max}
                          intentLabel={intentLabel}
                          active={selected === region}
                          dark={dark}
                          onClick={() => setSelected(selected === region ? null : region)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t pt-5" style={{ borderColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Low</span>
                  <div className="h-2 w-40 rounded-full" style={{ background: 'linear-gradient(90deg, rgba(56,189,248,0.4), rgb(245,158,11), rgb(239,68,68))' }} />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">High</span>
                </div>
                <p className="text-[11px] text-slate-500">Showing <span className="font-bold text-[#E87A20]">{intentLabel}</span> · tap a region for provinces</p>
              </div>
            </motion.div>

            {/* Side column: drill-down + note */}
            <motion.div variants={fade} initial="hidden" animate="show" className="space-y-5 lg:col-span-4">
              {/* Province drill-down */}
              <div data-testid="province-breakdown" className={`rounded-2xl border p-5 sm:p-6 ${glass}`}>
                {selected ? (
                  <>
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <h3 className="text-lg font-bold leading-tight">{selected}</h3>
                      <span className="shrink-0 rounded-full bg-[#E87A20]/15 px-3 py-1 text-xs font-bold text-[#E87A20]">{countFor(selected)}</span>
                    </div>
                    {selectedProvinces.length === 0 ? (
                      <p className="text-sm text-slate-500">No {intentLabel.toLowerCase()} recorded in this region yet.</p>
                    ) : (
                      <div className="divide-y" style={{ borderColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                        {selectedProvinces.map(({ p, c }) => {
                          const pmax = Math.max(1, ...selectedProvinces.map((x) => x.c));
                          const t = c / pmax;
                          const [r, g, b] = heatColor(Math.max(t, 0.12));
                          return (
                            <div key={p} data-testid="province-row" className="flex items-center gap-3 py-2.5">
                              <span className="w-28 flex-shrink-0 truncate text-sm font-semibold">{p}</span>
                              <div className="relative h-5 flex-1 overflow-hidden rounded-none border" style={{ borderColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
                                <div className="h-full" style={{ width: `${Math.max(8, t * 100)}%`, backgroundColor: `rgba(${r},${g},${b},0.85)` }} />
                              </div>
                              <span className="w-6 flex-shrink-0 text-right text-sm font-black">{c}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <MapPinned className="mb-3 h-8 w-8 text-slate-500/60" />
                    <p className="text-sm font-semibold text-slate-400">Select a region</p>
                    <p className="mt-1 text-xs text-slate-500">Tap any tile on the map to see its province-level breakdown.</p>
                  </div>
                )}
              </div>

              {/* No-region note */}
              {noRegion > 0 && (
                <div data-testid="territory-unspecified" className={`flex items-start gap-2 rounded-2xl border border-dashed p-4 text-xs ${dark ? 'border-white/15 text-slate-400' : 'border-slate-300 text-slate-500'}`}>
                  <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                  <span><strong className="text-slate-300">{noRegion}</strong> {noRegion === 1 ? 'inquiry' : 'inquiries'} under “{intentLabel}” carried no location (an address is only requested for free-sample shipments).</span>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {!loading && data?.total === 0 && (
          <p className="mt-10 text-center text-sm text-slate-500">No inquiries captured yet. The heatmap populates as contact-form submissions arrive.</p>
        )}
      </main>
    </div>
  );
};

export default TerritoryInsightsPage;
